import dayjs from 'dayjs'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate } from 'react-router-dom'
import styled from 'styled-components'

import { type Referral } from 'api/types/referrals'
import placeholderAvatar from 'assets/images/user_placeholder.png'
import * as Icons from 'assets/svg/sprite'
import { toFixedNumber } from 'helpers/util'

type FanItemProps = {
  fan: Referral
  isCreator?: boolean
}

export const FanItem = ({ fan, isCreator = false }: FanItemProps) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const avatar = (
    <Avatar
      src={fan.avatar?.url || placeholderAvatar}
      onError={(e) => {
        e.currentTarget.src = placeholderAvatar
      }}
    />
  )
  const coverUrl =
    !Array.isArray(fan.cover) && fan.cover?.url
      ? fan.cover.url
      : undefined
  return (
    <Container>
      <UpperSection
        $backgroundImage={coverUrl}
        $clickable={isCreator && !!fan.username}
        onClick={() => {
          if (isCreator && fan.username) navigate(`/${fan.username}`)
        }}
      >
        {isCreator && fan.username ? (
          <AvatarLink to={`/${fan.username}`}>{avatar}</AvatarLink>
        ) : (
          avatar
        )}

        <UserInfoContainer>
          <UserInfo>
            <UserName>{fan.name}</UserName>
            <UserHandle>@{fan.username || fan.name}</UserHandle>
          </UserInfo>

          <ButtonsContainer onClick={(e) => e.stopPropagation()}>
            {fan.show_message_button ? (
              <>
                <Button>
                  <Icons.IconTwoPeopleOutline />
                </Button>

                <Link to={`/chat/${fan.id}`}>
                  <Button>
                    <Icons.IconChatOutline color='#2894FF' />
                  </Button>
                </Link>
              </>
            ) : null}
          </ButtonsContainer>
        </UserInfoContainer>
      </UpperSection>

      {!isCreator && (
        <HorizontalLineContainer>
          <HorizontalLine />
        </HorizontalLineContainer>
      )}

      <LowerSection>
        <Value>
          {t('DateRegistered')}&nbsp;
          <span>{dayjs(fan.date_registered).format('ll')}</span>
        </Value>

        <Value $big>
          {t('earnings')}&nbsp;
          <span>${toFixedNumber(fan.earnings, 2)}</span>
        </Value>
      </LowerSection>
    </Container>
  )
}

const Container = styled.div`
  background: linear-gradient(89.85deg, #fbfbfb 0.1%, #ffffff 102.02%);
  box-shadow: 0px 4px 20px rgba(0, 0, 0, 0.05);
  border-radius: 2rem;
  margin-bottom: 0.9rem;
  overflow: hidden;
`

type UpperSectionProps = {
  $backgroundImage?: string
  $clickable?: boolean
}
const FALLBACK_COVER =
  'linear-gradient(135deg, #00cfd1, #09192d 40%, #8a003c, #ff003c)'

const UpperSection = styled.div<UpperSectionProps>`
  height: 100px;
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  position: relative;
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};
  background-image: ${({ $backgroundImage }) =>
    $backgroundImage?.trim()
      ? `url("${$backgroundImage}"), ${FALLBACK_COVER}`
      : FALLBACK_COVER};
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
`

const UserInfoContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  padding: 1.5rem 1rem 1.5rem 9rem;
  background-color: rgba(0, 0, 0, 0.38);
  position: relative;
  z-index: 1;
`

const AvatarLink = styled(Link)`
  position: absolute;
  top: 50%;
  left: 1rem;
  transform: translateY(-50%);
  z-index: 2;
  display: block;
  width: 72px;
  height: 72px;
  border-radius: 50%;
`

const Avatar = styled.img`
  height: 72px;
  width: 72px;
  border-radius: 50%;
  border: 2px solid #ffffff;
  box-sizing: border-box;
  object-fit: cover;
  object-position: center;
  display: block;
  background: #fff;
`

const UserInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`

const UserName = styled.p`
  line-height: 18px;
  font-size: 14px;
  font-weight: 600;
  font-family: Montserrat;
  color: #fff;
`

const UserHandle = styled.p`
  line-height: 18px;
  font-size: 1.4rem;
  font-weight: 600;
  font-family: Montserrat;
  color: #fff;
`

const ButtonsContainer = styled.div`
  display: flex;
  gap: 1rem;
`

const Button = styled.button`
  width: 4rem;
  height: 4rem;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: #fff;
  border: 0.1rem solid #f4f4f4;
  border-radius: 1rem;
  cursor: pointer;
`

const HorizontalLineContainer = styled.div`
  padding: 0 1rem;
`

const HorizontalLine = styled.hr`
  height: 1px;
  background-color: #d9d9d9;
  border: none;
`

const LowerSection = styled.div`
  padding: 1.5rem 1rem;
  display: flex;
  justify-content: space-between;
  align-items: stretch;
`

const Value = styled.p<{ $big?: boolean }>`
  display: inline-flex;
  align-items: center;
  font-family: Montserrat;
  font-size: 12px;
  font-weight: 400;
  color: #b0b0b0;
  line-height: 1;

  span {
    margin-left: 0.4rem;
    font-size: ${({ $big }) => ($big ? '16px' : '12px')};
    font-weight: 600;
    line-height: 1;
    color: #262a33;
  }
`